"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MenuItemImpl = MenuItemImpl;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_router_1 = require("react-router");
const react_router_dom_1 = require("react-router-dom");
const Avatar_1 = require("../Avatar");
const Icon_1 = require("../Icon");
const Tooltip_1 = require("../Tooltip");
const Css_1 = require("../../Css");
const utils_1 = require("../../utils");
const defaultTestId_1 = require("../../utils/defaultTestId");
function MenuItemImpl(props) {
    const { item, state, onClose, contrast } = props;
    const menuItem = item.value;
    if (!menuItem) {
        return null;
    }
    const { disabled, onClick, label, destructive } = menuItem;
    const isDisabled = Boolean(disabled);
    const isSelected = state.selectionManager.selectedKeys.has(label);
    const isFocused = state.selectionManager.focusedKey === item.key;
    // TODO: validate this eslint-disable with https://app.shortcut.com/homebound-team/story/40045
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ref = (0, react_1.useRef)(null);
    // TODO: validate this eslint-disable with https://app.shortcut.com/homebound-team/story/40045
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const history = (0, react_router_1.useHistory)();
    // TODO: validate this eslint-disable with https://app.shortcut.com/homebound-team/story/40045
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)({});
    // TODO: validate this eslint-disable with https://app.shortcut.com/homebound-team/story/40045
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const tid = (0, utils_1.useTestIds)(props);
    // TODO: validate this eslint-disable with https://app.shortcut.com/homebound-team/story/40045
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { menuItemProps } = (0, react_aria_1.useMenuItem)({
        key: item.key,
        isDisabled,
        onAction: () => {
            if (typeof onClick === "string") {
                // if it is an absolute URL, then open in new window. Assuming this should leave the App
                if ((0, utils_1.isAbsoluteUrl)(onClick)) {
                    // We want to do `window.open(url, "_blank", "noopener,noreferrer")` but that Safari treats
                    // that as "open in new window", this happens when safari has the "Open pages in tabs instead of windows" set to "Automatically" (which is the default)
                    // see https://support.apple.com/guide/safari/tabs-ibrw1045/mac (Open pages in tabs instead of windows) for other behaviors
                    //
                    // So we do this instead, and at least null out the opener
                    // as a way to manually mimic the `"noopener"` flag.
                    window.open(onClick, "_blank").opener = null;
                    return;
                }
                // Otherwise, it is a relative URL and we'll assume it is still within the App.
                history.push(onClick);
                return;
            }
            onClick && onClick();
        },
        onClose,
    }, state, ref);
    return ((0, jsx_runtime_1.jsx)("li", { ...menuItemProps, ...hoverProps, ref: ref, css: {
            ...Css_1.Css.df.aic.py1.px2.cursorPointer.outline0.mh("42px").sm.$,
            ...(menuItem.hasDivider ? Css_1.Css.bb.bcGray700.$ : {}),
            ...(!isDisabled && isHovered ? (contrast ? Css_1.Css.bgGray800.$ : Css_1.Css.bgGray100.$) : {}),
            ...(isFocused ? Css_1.Css.add("boxShadow", `inset 0 0 0 1px ${Css_1.Palette.Blue700}`).$ : {}),
            ...(isDisabled ? Css_1.Css.gray500.cursorNotAllowed.$ : {}),
            ...(destructive ? Css_1.Css.red600.$ : {}),
            ...(isSelected ? Css_1.Css.fw5.$ : {}),
        }, ...tid[(0, defaultTestId_1.defaultTestId)(menuItem.label)], children: (0, Tooltip_1.maybeTooltip)({
            title: (0, Tooltip_1.resolveTooltip)(disabled),
            placement: "right",
            children: renderMenuItem(menuItem, isSelected, isDisabled, contrast),
        }) }));
}
function renderMenuItem(menuItem, isSelected, isDisabled, contrast) {
    return ((0, jsx_runtime_1.jsxs)("div", { css: Css_1.Css.df.w100.aic.jcsb.gap2.$, children: [(0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.df.aic.$, children: maybeWrapInLink(menuItem.onClick, isIconMenuItem(menuItem) ? ((0, jsx_runtime_1.jsx)(IconMenuItem, { ...menuItem })) : isImageMenuItem(menuItem) ? ((0, jsx_runtime_1.jsx)(ImageMenuItem, { ...menuItem })) : (menuItem.label), isDisabled) }), isSelected && ((0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: "check", color: !contrast ? (isDisabled ? Css_1.Palette.Gray400 : Css_1.Palette.Blue700) : isDisabled ? Css_1.Palette.Gray500 : Css_1.Palette.White }))] }));
}
function ImageMenuItem(item) {
    const { src, size = 24, label, isAvatar = false } = item;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fs0.mr2.$, children: isAvatar ? ((0, jsx_runtime_1.jsx)(Avatar_1.Avatar, { src: src, name: label, size: size === 24 ? "sm" : "lg" })) : ((0, jsx_runtime_1.jsx)("img", { width: size, src: src, css: Css_1.Css.br4.$, alt: label })) }), label] }));
}
function IconMenuItem(item) {
    const { icon, label } = item;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fs0.mr2.$, children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: icon }) }), label] }));
}
function maybeWrapInLink(onClick, content, disabled) {
    if (disabled || typeof onClick !== "string") {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: content });
    }
    return (0, utils_1.isAbsoluteUrl)(onClick) ? ((0, jsx_runtime_1.jsxs)("a", { href: onClick, target: "_blank", rel: "noopener noreferrer", className: "navLink", css: Css_1.Css.df.aic.jcsb.w100.$, children: [content, (0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fs0.ml2.$, children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: "linkExternal" }) })] })) : ((0, jsx_runtime_1.jsx)(react_router_dom_1.Link, { className: "navLink", to: onClick, children: content }));
}
function isIconMenuItem(item) {
    return item && typeof item === "object" && "icon" in item;
}
function isImageMenuItem(item) {
    return item && typeof item === "object" && "src" in item;
}
